import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { toast, confirmar } from '../lib/toast';
import { Card } from './ui/Card';
import { Spinner } from './ui/Spinner';

type TipoArchivo = 'imagen_principal' | 'imagen_adicional' | 'ficha' | 'user_guide' | 'specs' | 'otro';

type ArchivoRow = {
  id: string;
  tipo: TipoArchivo;
  storage_path: string;
  nombre_original: string;
  mime_type: string | null;
  bytes: number | null;
  ordering: number;
  created_at: string;
  url: string | null;
};

type ListResponse = {
  data: ArchivoRow[];
  storage_configured: boolean;
};

async function uploadFile(productoProveedorId: string, tipo: TipoArchivo, file: File): Promise<void> {
  const { data: sess } = await supabase.auth.getSession();
  const token = sess.session?.access_token;
  const fd = new FormData();
  fd.append('tipo', tipo);
  fd.append('file', file);
  const res = await fetch(`/api/productos-proveedor/${productoProveedorId}/archivos`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    body: fd,
  });
  if (!res.ok) {
    const txt = await res.text();
    throw new Error(`Upload falló (${res.status}): ${txt}`);
  }
}

/**
 * Lee imágenes del portapapeles. Usa navigator.clipboard.read() si está
 * disponible (requiere permiso del usuario por el botón explícito), si no
 * cae a no devolver nada — el usuario puede usar Ctrl+V que dispara onPaste.
 */
async function readImagesFromClipboard(): Promise<File[]> {
  if (!navigator.clipboard?.read) return [];
  try {
    const items = await navigator.clipboard.read();
    const files: File[] = [];
    for (const item of items) {
      const imgType = item.types.find((t) => t.startsWith('image/'));
      if (!imgType) continue;
      const blob = await item.getType(imgType);
      const ext = imgType.split('/')[1] || 'png';
      files.push(new File([blob], `clipboard-${Date.now()}.${ext}`, { type: imgType }));
    }
    return files;
  } catch {
    return [];
  }
}

function filesFromPasteEvent(e: ClipboardEvent | React.ClipboardEvent): File[] {
  const items = e.clipboardData?.items;
  if (!items) return [];
  const files: File[] = [];
  for (const item of Array.from(items)) {
    if (item.kind !== 'file') continue;
    const blob = item.getAsFile();
    if (blob && blob.type.startsWith('image/')) {
      const ext = blob.type.split('/')[1] || 'png';
      files.push(new File([blob], blob.name || `pasted-${Date.now()}.${ext}`, { type: blob.type }));
    }
  }
  return files;
}

/**
 * Devuelve cualquier archivo del clipboard (no solo imágenes). Usado por los
 * slots de documentos (ficha/user_guide/specs) que aceptan PDF, DOC, XLS y
 * también capturas de pantalla pegadas con Ctrl+V.
 */
function anyFilesFromPasteEvent(e: ClipboardEvent | React.ClipboardEvent): File[] {
  const items = e.clipboardData?.items;
  if (!items) return [];
  const files: File[] = [];
  for (const item of Array.from(items)) {
    if (item.kind !== 'file') continue;
    const blob = item.getAsFile();
    if (!blob) continue;
    const ext = (blob.type.split('/')[1] ?? 'bin').replace('+', '-');
    files.push(new File([blob], blob.name || `pasted-${Date.now()}.${ext}`, { type: blob.type || 'application/octet-stream' }));
  }
  return files;
}

async function deleteFile(productoProveedorId: string, archivoId: string): Promise<void> {
  const { data: sess } = await supabase.auth.getSession();
  const token = sess.session?.access_token;
  const res = await fetch(`/api/productos-proveedor/${productoProveedorId}/archivos/${archivoId}`, {
    method: 'DELETE',
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
  if (!res.ok) {
    const txt = await res.text();
    throw new Error(`Delete falló (${res.status}): ${txt}`);
  }
}

// ─────────────────────────────────────────────────────────────────────
// Imagen principal (1 sola)
// ─────────────────────────────────────────────────────────────────────

export function ImagenPrincipalCard({ productoProveedorId }: { productoProveedorId: string }) {
  const qc = useQueryClient();
  const lista = useQuery<ListResponse>({
    queryKey: ['producto-proveedor-archivos', productoProveedorId],
    queryFn: async () => {
      const { data: sess } = await supabase.auth.getSession();
      const token = sess.session?.access_token;
      const res = await fetch(`/api/productos-proveedor/${productoProveedorId}/archivos`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      return res.json() as Promise<ListResponse>;
    },
  });
  const principal = lista.data?.data.find((a) => a.tipo === 'imagen_principal') ?? null;
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  const subir = useMutation({
    mutationFn: (file: File) => uploadFile(productoProveedorId, 'imagen_principal', file),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['producto-proveedor-archivos', productoProveedorId] }),
  });

  const borrar = useMutation({
    mutationFn: (id: string) => deleteFile(productoProveedorId, id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['producto-proveedor-archivos', productoProveedorId] }),
  });

  // Pegar imagen del portapapeles cuando esta card está montada y el foco
  // no está ni en un input ni en un slot que ya reclama el paste para sí
  // (data-paste-claimed). Esto evita que pegar en "Imágenes adicionales" o
  // en Ficha/UserGuide/Specs sobrescriba la imagen principal.
  useEffect(() => {
    function onPaste(e: ClipboardEvent) {
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return;
      if (target && typeof target.closest === 'function' && target.closest('[data-paste-claimed="true"]')) return;
      const files = filesFromPasteEvent(e);
      if (files.length > 0) {
        e.preventDefault();
        subir.mutate(files[0]);
      }
    }
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [subir]);

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f && f.type.startsWith('image/')) subir.mutate(f);
  };

  const pegarBoton = async () => {
    const files = await readImagesFromClipboard();
    if (files.length > 0) subir.mutate(files[0]);
    else toast.info('No hay imagen en el portapapeles', 'Copia una imagen y vuelve a intentarlo.');
  };

  return (
    <Card
      title="Imagen principal"
      description="Una sola foto destacada. Sube fichero, pega con Ctrl+V (anywhere) o arrastra."
    >
      {lista.isLoading ? <Spinner /> : (
        <div className="flex flex-col items-center gap-3">
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            className={`relative h-48 w-48 overflow-hidden rounded-md border-2 ${dragOver ? 'border-sky-500 bg-sky-50' : 'border-slate-200 bg-slate-50'}`}
          >
            {principal?.url
              ? <img src={principal.url} alt="Principal" className="h-full w-full object-contain" />
              : <div className="flex h-full w-full items-center justify-center text-center text-xs italic text-slate-400">{dragOver ? 'Soltar aquí' : 'Sin imagen. Arrastra, pega o sube.'}</div>}
          </div>
          <div className="flex flex-wrap justify-center gap-2">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={subir.isPending}
              className="cursor-pointer rounded-md border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-800 hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-50"
            >{subir.isPending ? 'Subiendo…' : (principal ? 'Reemplazar' : 'Subir')}</button>
            <button
              type="button"
              onClick={pegarBoton}
              disabled={subir.isPending}
              className="cursor-pointer rounded-md border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-800 hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-50"
              title="Lee del portapapeles. También funciona Ctrl+V en cualquier sitio de esta página."
            >📋 Pegar</button>
            {principal && (
              <button
                type="button"
                onClick={async () => { if (await confirmar({ title: '¿Borrar imagen principal?', confirmLabel: 'Borrar', danger: true })) borrar.mutate(principal.id); }}
                className="cursor-pointer rounded-md border border-rose-200 bg-white px-3 py-1.5 text-xs font-semibold text-rose-700 hover:bg-rose-50"
              >Borrar</button>
            )}
            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) subir.mutate(f); e.target.value = ''; }}
            />
          </div>
          {subir.isError && (
            <p className="text-xs text-rose-700">{(subir.error as Error)?.message ?? 'Error subiendo imagen'}</p>
          )}
        </div>
      )}
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Galería de imágenes adicionales (N fotos)
// ─────────────────────────────────────────────────────────────────────

/**
 * Fila de slots "Pega aquí" focusables que permiten encadenar varios Ctrl+V.
 * Cada slot captura su propio paste; tras subir, mueve el foco al siguiente
 * slot vacío para poder pegar otra imagen sin tocar el ratón. Si el portapapeles
 * trae varias imágenes en un solo paste, también las acepta todas a la vez.
 */
function MultiPasteSlots({
  count,
  onFiles,
  disabled,
}: {
  count: number;
  onFiles: (files: File[]) => void;
  disabled?: boolean;
}) {
  const refs = useRef<(HTMLDivElement | null)[]>([]);
  const [usados, setUsados] = useState<boolean[]>(() => Array.from({ length: count }, () => false));

  const handlePaste = (e: React.ClipboardEvent, idx: number) => {
    if (disabled) return;
    const files = filesFromPasteEvent(e);
    if (files.length === 0) return;
    e.preventDefault();
    e.stopPropagation();
    onFiles(files);
    // Marcar este slot como usado y mover el foco al siguiente disponible.
    setUsados((prev) => {
      const next = [...prev];
      next[idx] = true;
      // Busca el primer slot libre a partir del siguiente; si no hay,
      // queda en el último y el usuario puede seguir pegando ahí.
      const search = next.slice(idx + 1).findIndex((u) => !u);
      const nextIdx = search >= 0 ? idx + 1 + search : Math.min(idx + 1, count - 1);
      // Esperar a que React pinte antes de mover foco.
      setTimeout(() => refs.current[nextIdx]?.focus(), 0);
      return next;
    });
  };

  const reset = () => setUsados(Array.from({ length: count }, () => false));

  return (
    <div data-paste-claimed="true" className="rounded-md border border-sky-200 bg-sky-50/30 p-2">
      <div className="mb-1.5 flex items-center justify-between">
        <p className="text-[11px] text-sky-800">
          Haz clic en un cuadro y pulsa <span className="font-mono font-semibold">Ctrl+V</span> para pegar.
          Cada paste sube una imagen y avanza al siguiente cuadro.
        </p>
        {usados.some(Boolean) && (
          <button
            type="button"
            onClick={reset}
            className="cursor-pointer text-[10px] text-sky-700 hover:underline"
          >
            Liberar cuadros
          </button>
        )}
      </div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-6">
        {Array.from({ length: count }).map((_, i) => {
          const usado = usados[i];
          return (
            <div
              key={i}
              ref={(el) => { refs.current[i] = el; }}
              tabIndex={disabled ? -1 : 0}
              onPaste={(e) => handlePaste(e, i)}
              className={`flex h-16 cursor-text items-center justify-center rounded-md border-2 border-dashed text-xs outline-none transition ${
                usado
                  ? 'border-emerald-300 bg-emerald-50 text-emerald-700'
                  : 'border-slate-300 bg-white text-slate-500 hover:border-sky-400 focus:border-sky-500 focus:bg-sky-50 focus:text-sky-700'
              }`}
              title={usado ? 'Imagen pegada — puedes seguir pegando otra' : 'Click + Ctrl+V'}
            >
              {usado ? '✓ Subida — pega otra' : `Pega aquí (${i + 1})`}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function GaleriaAdicionalCard({ productoProveedorId }: { productoProveedorId: string }) {
  const qc = useQueryClient();
  const lista = useQuery<ListResponse>({
    queryKey: ['producto-proveedor-archivos', productoProveedorId],
    queryFn: async () => {
      const { data: sess } = await supabase.auth.getSession();
      const token = sess.session?.access_token;
      const res = await fetch(`/api/productos-proveedor/${productoProveedorId}/archivos`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      return res.json() as Promise<ListResponse>;
    },
  });
  const fotos = lista.data?.data.filter((a) => a.tipo === 'imagen_adicional') ?? [];
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  const subir = useMutation({
    mutationFn: async (files: File[]) => {
      for (const f of files) {
        await uploadFile(productoProveedorId, 'imagen_adicional', f);
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['producto-proveedor-archivos', productoProveedorId] }),
  });

  const borrar = useMutation({
    mutationFn: (id: string) => deleteFile(productoProveedorId, id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['producto-proveedor-archivos', productoProveedorId] }),
  });

  const pegarBoton = async () => {
    const files = await readImagesFromClipboard();
    if (files.length > 0) subir.mutate(files);
    else toast.info('No hay imagen en el portapapeles');
  };

  return (
    <Card
      title={`Imágenes adicionales (${fotos.length})`}
      description="Galería complementaria. Soporta arrastrar y soltar, pegar desde portapapeles y pegar varias seguidas con Ctrl+V."
      actions={
        <button
          type="button"
          onClick={pegarBoton}
          disabled={subir.isPending}
          className="cursor-pointer rounded-md border border-amber-300 bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-800 hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-50"
        >📋 Pegar</button>
      }
    >
      <div className="mb-3">
        <MultiPasteSlots count={6} onFiles={(fs) => subir.mutate(fs)} disabled={subir.isPending} />
      </div>
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          const fs = Array.from(e.dataTransfer.files ?? []).filter((f) => f.type.startsWith('image/'));
          if (fs.length > 0) subir.mutate(fs);
        }}
        className={`flex flex-wrap gap-3 rounded-md p-2 ${dragOver ? 'bg-sky-50 ring-2 ring-sky-300' : ''}`}
      >
        {fotos.map((f) => (
          <div key={f.id} className="group relative">
            <div className="h-24 w-24 overflow-hidden rounded-md border border-slate-200 bg-slate-50">
              {f.url && <img src={f.url} alt={f.nombre_original} className="h-full w-full object-contain" />}
            </div>
            <button
              type="button"
              onClick={async () => { if (await confirmar({ title: '¿Borrar foto?', confirmLabel: 'Borrar', danger: true })) borrar.mutate(f.id); }}
              className="absolute -right-1 -top-1 hidden h-5 w-5 cursor-pointer items-center justify-center rounded-full bg-rose-600 text-xs font-bold text-white hover:bg-rose-700 group-hover:flex"
            >×</button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={subir.isPending}
          className="flex h-24 w-24 cursor-pointer items-center justify-center rounded-md border-2 border-dashed border-slate-300 bg-white text-2xl text-slate-400 hover:border-slate-400 hover:text-slate-600 disabled:cursor-not-allowed disabled:opacity-50"
        >{subir.isPending ? '…' : '+'}</button>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => { const fs = e.target.files; if (fs && fs.length > 0) subir.mutate(Array.from(fs)); e.target.value = ''; }}
        />
      </div>
      {subir.isError && (
        <p className="mt-2 text-xs text-rose-700">{(subir.error as Error)?.message ?? 'Error subiendo'}</p>
      )}
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Slots únicos (ficha, user_guide, specs)
// ─────────────────────────────────────────────────────────────────────

function FileIcon({ mime }: { mime: string | null }) {
  const ext = (() => {
    if (!mime) return 'FILE';
    if (mime.includes('pdf')) return 'PDF';
    if (mime.includes('word') || mime.includes('msword') || mime.includes('officedocument.wordprocessingml')) return 'DOC';
    if (mime.includes('excel') || mime.includes('spreadsheetml')) return 'XLS';
    return mime.split('/')[1]?.slice(0, 3).toUpperCase() || 'FILE';
  })();
  const colors: Record<string, string> = {
    PDF: 'bg-rose-500',
    DOC: 'bg-sky-600',
    XLS: 'bg-emerald-600',
    FILE: 'bg-slate-500',
  };
  return (
    <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-md text-xs font-bold text-white ${colors[ext] ?? colors.FILE}`}>
      {ext}
    </div>
  );
}

function DocumentoSlotCard({
  productoProveedorId, tipo, titulo, accept, descripcion,
}: {
  productoProveedorId: string;
  tipo: TipoArchivo;
  titulo: string;
  accept: string;
  descripcion: string;
}) {
  const qc = useQueryClient();
  const lista = useQuery<ListResponse>({
    queryKey: ['producto-proveedor-archivos', productoProveedorId],
    queryFn: async () => {
      const { data: sess } = await supabase.auth.getSession();
      const token = sess.session?.access_token;
      const res = await fetch(`/api/productos-proveedor/${productoProveedorId}/archivos`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      return res.json() as Promise<ListResponse>;
    },
  });
  const doc = lista.data?.data.find((a) => a.tipo === tipo) ?? null;
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const subir = useMutation({
    mutationFn: (file: File) => uploadFile(productoProveedorId, tipo, file),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['producto-proveedor-archivos', productoProveedorId] }),
  });
  const borrar = useMutation({
    mutationFn: (id: string) => deleteFile(productoProveedorId, id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['producto-proveedor-archivos', productoProveedorId] }),
  });

  // Es un slot único: solo se queda con el primer archivo del paste/drop.
  // Reemplaza al existente si lo había (el backend hace soft replace por
  // unique(producto_proveedor_id, tipo) cuando aplica).
  const aceptarUno = (files: File[] | FileList | null) => {
    if (!files) return;
    const arr = Array.from(files);
    if (arr.length === 0) return;
    subir.mutate(arr[0]);
  };

  return (
    <div
      tabIndex={0}
      data-paste-claimed="true"
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        aceptarUno(e.dataTransfer.files);
      }}
      onPaste={(e) => {
        const files = anyFilesFromPasteEvent(e);
        if (files.length === 0) return;
        e.preventDefault();
        e.stopPropagation();
        aceptarUno(files);
      }}
      className={`rounded-lg outline-none ring-offset-1 transition focus:ring-2 focus:ring-sky-300 ${
        dragOver ? 'ring-2 ring-sky-400' : ''
      }`}
      title="Arrastra un archivo aquí o pulsa Ctrl+V para pegar"
    >
      <Card
        title={titulo}
        description={`${descripcion} · Arrastra el archivo o pulsa Ctrl+V para pegar una captura.`}
      >
        {doc ? (
          <div className="flex items-center gap-3">
            <FileIcon mime={doc.mime_type} />
            <div className="min-w-0 flex-1">
              {doc.url ? (
                <a href={doc.url} target="_blank" rel="noreferrer" className="block truncate cursor-pointer text-sm font-medium text-sky-700 hover:underline" title={doc.nombre_original}>
                  {doc.nombre_original}
                </a>
              ) : (
                <span className="block truncate text-sm font-medium text-slate-700" title={doc.nombre_original}>{doc.nombre_original}</span>
              )}
              <p className="text-xs text-slate-500">{doc.bytes != null ? `${Math.round(doc.bytes / 1024)} KB` : ''}</p>
            </div>
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={subir.isPending}
              className="cursor-pointer rounded-md border border-amber-300 bg-amber-50 px-2 py-1 text-xs font-semibold text-amber-800 hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-50"
            >{subir.isPending ? '…' : 'Reemplazar'}</button>
            <button
              type="button"
              onClick={async () => { if (await confirmar({ title: '¿Borrar archivo?', confirmLabel: 'Borrar', danger: true })) borrar.mutate(doc.id); }}
              className="cursor-pointer rounded-md border border-rose-200 bg-white px-2 py-1 text-xs font-semibold text-rose-700 hover:bg-rose-50"
            >×</button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={subir.isPending}
            className={`flex w-full cursor-pointer items-center justify-center rounded-md border-2 border-dashed px-3 py-4 text-sm font-medium transition ${
              dragOver
                ? 'border-sky-500 bg-sky-50 text-sky-700'
                : 'border-slate-300 bg-white text-slate-500 hover:border-slate-400 hover:text-slate-700'
            } disabled:cursor-not-allowed disabled:opacity-50`}
          >
            {subir.isPending ? 'Subiendo…' : dragOver ? 'Suelta para subir' : `+ Subir ${titulo} · arrastra o Ctrl+V`}
          </button>
        )}
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          className="hidden"
          onChange={(e) => { const f = e.target.files?.[0]; if (f) subir.mutate(f); e.target.value = ''; }}
        />
        {subir.isError && (
          <p className="mt-2 text-xs text-rose-700">{(subir.error as Error)?.message ?? 'Error subiendo'}</p>
        )}
      </Card>
    </div>
  );
}

const DOC_ACCEPT = '.pdf,.doc,.docx,.xls,.xlsx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

export function FichaCard({ productoProveedorId }: { productoProveedorId: string }) {
  return <DocumentoSlotCard productoProveedorId={productoProveedorId} tipo="ficha" titulo="Ficha de producto" accept={DOC_ACCEPT} descripcion="PDF, DOC o XLS." />;
}
export function UserGuideCard({ productoProveedorId }: { productoProveedorId: string }) {
  return <DocumentoSlotCard productoProveedorId={productoProveedorId} tipo="user_guide" titulo="User Guide" accept={DOC_ACCEPT} descripcion="Manual de usuario del fabricante." />;
}
export function SpecsCard({ productoProveedorId }: { productoProveedorId: string }) {
  return <DocumentoSlotCard productoProveedorId={productoProveedorId} tipo="specs" titulo="Specs sheet" accept={DOC_ACCEPT} descripcion="Hoja técnica o spec sheet." />;
}
